import { useMemo, useState } from "react"
import { HttpMethodBadge } from "./http-method-badge"
import { ErrorRateSparkline } from "./error-rate-sparkline"
import { fmtCompactRelative } from "@/lib/format"
import { cn } from "@/lib/cn"
import type { Endpoint, HttpMethod } from "@/types/endpoint"

type Props = {
  endpoints: Endpoint[]
  selectedId: number | null
  onSelect: (id: number) => void
  onChartClick: (endpoint: Endpoint) => void
}

const METHOD_OPTIONS: ("ALL" | HttpMethod)[] = ["ALL", "GET", "POST", "PUT", "PATCH", "DELETE"]
const PAGE_SIZE = 12

export function EndpointList({ endpoints, selectedId, onSelect, onChartClick }: Props) {
  const [search, setSearch] = useState("")
  const [method, setMethod] = useState<"ALL" | HttpMethod>("ALL")
  const [page, setPage] = useState(0)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return endpoints.filter((e) => {
      if (method !== "ALL" && e.httpMethod !== method) return false
      if (!q) return true
      return (
        e.name.toLowerCase().includes(q) ||
        e.key.toLowerCase().includes(q) ||
        e.pathTemplate.toLowerCase().includes(q)
      )
    })
  }, [endpoints, search, method])

  const pageStart = page * PAGE_SIZE
  const pageRows = filtered.slice(pageStart, pageStart + PAGE_SIZE)
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))

  return (
    <div className="flex h-full flex-col rounded-card border border-border bg-app-panel">
      <div className="border-b border-border p-4">
        <div className="mb-3 text-sm font-medium text-app-text">Endpoints</div>
        <div className="flex gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0) }}
            placeholder="Search endpoints…"
            className="min-w-0 flex-1 rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text placeholder:text-app-muted focus:border-brand-red focus:outline-none"
          />
          <select
            value={method}
            onChange={(e) => { setMethod(e.target.value as typeof method); setPage(0) }}
            className="rounded-control border border-border bg-app-bg px-2 py-2 text-xs text-app-text focus:border-brand-red focus:outline-none"
            title="Filter by HTTP method"
          >
            {METHOD_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      {pageRows.length === 0 ? (
        <div className="flex-1 p-6 text-sm text-app-muted">
          {endpoints.length === 0 ? "No endpoints yet — click + New endpoint to add one." : "No matches."}
        </div>
      ) : (
        <div className="flex-1 divide-y divide-border overflow-y-auto">
          <div className="grid grid-cols-[1.4fr_64px_2fr_170px_110px] gap-3 px-4 py-2 text-[11px] uppercase tracking-wide text-app-muted">
            <div>Name</div>
            <div>Method</div>
            <div>Path Template</div>
            <div>Error Rate (30d)</div>
            <div className="text-right">Last Hit</div>
          </div>
          {pageRows.map((e) => (
            <div
              key={e.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(e.id)}
              onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); onSelect(e.id) } }}
              className={cn(
                "grid w-full cursor-pointer grid-cols-[1.4fr_64px_2fr_170px_110px] items-center gap-3 px-4 py-3 text-left text-sm transition outline-none hover:bg-white/5 focus-visible:bg-white/5 focus-visible:ring-1 focus-visible:ring-brand-red/40",
                e.id === selectedId && "bg-brand-redSoft/30 ring-1 ring-brand-red/40"
              )}
            >
              <div className="flex min-w-0 flex-col">
                <div className="flex items-center gap-2">
                  {e.last24hErrorCount > 0 && (
                    <span title={`${e.last24hErrorCount} error(s) in the last 24h`} className="text-status-error">⚠</span>
                  )}
                  <span className="truncate font-medium text-app-text">{e.name}</span>
                </div>
                <span className="truncate font-mono text-[11px] text-app-muted">{e.key}</span>
              </div>
              <HttpMethodBadge method={e.httpMethod} />
              <div className="truncate font-mono text-xs text-app-muted">{e.pathTemplate}</div>
              <ErrorRateSparkline
                errorPctByDay={e.errorPctByDay}
                callsByDay={e.callsByDay}
                onClick={() => onChartClick(e)}
              />
              <div className="text-right text-xs text-app-muted">{fmtCompactRelative(e.lastHitAt)}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between border-t border-border px-4 py-2 text-xs text-app-muted">
        <div>
          {filtered.length === 0
            ? "No results"
            : `Showing ${pageStart + 1} to ${Math.min(pageStart + PAGE_SIZE, filtered.length)} of ${filtered.length} endpoints`}
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
            className="rounded-control border border-border px-2 py-1 disabled:opacity-40">‹</button>
          <span className="rounded-control border border-brand-red bg-brand-redSoft/30 px-2 py-1 text-app-text">{page + 1}</span>
          <button type="button" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
            className="rounded-control border border-border px-2 py-1 disabled:opacity-40">›</button>
        </div>
      </div>
    </div>
  )
}
